import { FixedContent, ScrollContent } from '@components/AppShell'
import { RouteLevelErrorComponent } from '@components/RouteLevelErrorComponent'
import { auroraMcpUrl, auroraWsUrl, domain } from '@config/environment'
import { adminRoutes } from '@features/admin'
import { RequireAuthGuard } from '@features/auth'
import { RequireAcceptEulaGuard } from '@features/auth/components/RequireAcceptEulaGuard'
import { DealerFieldListRedirect } from '@features/dealer/components/DealerFieldListRedirect'
import { DealerOnlyGuard } from '@features/dealer/components/DealerOnlyGuard'
import { dealerRoutes } from '@features/dealer/routes'
import { RedirectToOrgHome } from '@features/fields/routes/RedirectToOrgHome'
import { useAuroraChatStore } from '@features/misc/auroraChatStore'
import AuroraClientHandlers from '@features/misc/components/AuroraClientHandlers'
import { RequireActiveOrgPlanGuard } from '@features/operations/components/RequireActiveOrgPlanGuard'
import { RequireOrgAccessGuard } from '@features/operations/components/RequireOrgAccessGuard'
import { operationRoutes } from '@features/operations/routes'
import { supportRoutes } from '@features/support/routes'
import { AuroraClientType } from 'api-validations/aurora'
import { lazy, Suspense } from 'react'
import { Outlet, type RouteObject } from 'react-router'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { AuroraContextProvider } from 'shared-ui/aurora'
import { NotFound } from 'shared-ui/components'
import { EmissaryWebsocketProvider } from 'shared-ui/operation-wide-data'

import { lazy as routeLazy } from './lazy'
import { path } from './paths'

const errorElement = <RouteLevelErrorComponent />

const AuroraChatComponent = lazy(() => import('@features/misc/components/AuroraChat'))

const AuroraChatPanel: React.FC = () => {
  const orgCode = useOrgCodeOrThrow()
  const { isOpen, hasLoaded, close, chatKey, pendingFeedback } = useAuroraChatStore()
  if (!hasLoaded) return null
  return (
    <Suspense fallback={null}>
      <AuroraChatComponent
        key={`${orgCode}:${chatKey}`}
        isOpen={isOpen}
        onClose={close}
        initialFeedback={pendingFeedback}
      />
    </Suspense>
  )
}

const fixedHeightRoutes: RouteObject[] = [
  {
    element: <DealerFieldListRedirect />,
    errorElement,
    children: [
      {
        path: path.map,
        Component: routeLazy(() => import('@features/fields/routes/FieldLayout')),
        errorElement
      }
    ]
  },
  {
    element: <DealerOnlyGuard />,
    errorElement,
    children: [
      {
        path: path.dealerMap,
        Component: routeLazy(() => import('@features/dealer/routes/DealerMap')),
        errorElement
      }
    ]
  }
]

const defaultLayoutRoutes: {
  orgScoped: RouteObject[]
  general: RouteObject[]
} = {
  general: [
    {
      path: path.root,
      element: <RedirectToOrgHome />,
      errorElement
    },
    {
      path: path.pair,
      Component: routeLazy(() => import('@features/fields/routes/PairDevice')),
      errorElement
    },
    {
      path: path.connectionDetails,
      Component: routeLazy(() => import('@features/operations/routes/ConnectionDetails')),
      errorElement
    },
    {
      path: path.eula,
      Component: routeLazy(() => import('@features/misc/routes/EULA')),
      errorElement
    },
    {
      path: path.postSale,
      Component: routeLazy(() => import('@features/operations/routes/PostSale')),
      errorElement
    },
    {
      path: path.operationList,
      Component: routeLazy(() => import('@features/operations/routes/FullOperationsList')),
      errorElement
    },
    {
      path: '/admin',
      children: adminRoutes,
      errorElement
    },
    {
      path: '/support',
      children: supportRoutes,
      errorElement
    },
    {
      path: '*',
      element: <NotFound />,
      errorElement
    },
    {
      path: path.acceptInvite,
      Component: routeLazy(() => import('@features/operations/routes/AcceptInvite')),
      errorElement
    },
    {
      path: path.acceptHandoff,
      Component: routeLazy(() => import('@features/dealer/routes/AcceptHandoff')),
      errorElement
    },
    {
      path: path.acceptStreamCopy,
      Component: routeLazy(() => import('@features/operations/routes/AcceptStreamCopy')),
      errorElement
    },
    {
      path: path.gettingStarted,
      Component: routeLazy(() => import('@features/operations/routes/GettingStarted')),
      errorElement
    },
    {
      path: path.createOperation,
      Component: routeLazy(() => import('@features/operations/routes/CreateOperation')),
      errorElement
    },
    {
      path: path.updateEmailLogin,
      Component: routeLazy(() => import('@features/auth/routes/UpdateLogin')),
      errorElement
    },
    {
      path: path.dealerShare,
      Component: routeLazy(() => import('@features/dealer/routes/AcceptDealerShare')),
      errorElement
    }
  ],
  orgScoped: [
    {
      path: path.home,
      Component: routeLazy(() => import('@features/fields/routes/Homepage')),
      errorElement
    },
    {
      path: path.reporting,
      Component: routeLazy(() => import('@features/fields/routes/Reporting')),
      errorElement
    },
    {
      path: path.notifications,
      Component: routeLazy(() => import('@features/notifications/routes/NotificationsPage')),
      errorElement
    },
    {
      path: path.postLivestreamRedirect,
      Component: routeLazy(() => import('@features/operations/routes/PostLivestreamRedirect')),
      errorElement
    },

    {
      path: path.manageOperation,
      children: operationRoutes,
      errorElement
    },
    {
      path: path.dealerOperations,
      children: dealerRoutes,
      errorElement
    }
  ]
}

export const privateRoutes: RouteObject[] = [
  {
    element: <RequireAuthGuard />,
    children: [
      {
        element: <RequireActiveOrgPlanGuard />,
        children: [
          {
            path: '/:orgCode',
            element: (
              <AuroraContextProvider
                wsUrl={auroraWsUrl}
                mcpUrl={auroraMcpUrl}
                clientType={AuroraClientType.Web}
                commitSha={import.meta.env.VITE_COMMIT_SHA as string | undefined}
              >
                <EmissaryWebsocketProvider domain={domain}>
                  <Outlet />
                  <AuroraChatPanel />
                  <AuroraClientHandlers />
                </EmissaryWebsocketProvider>
              </AuroraContextProvider>
            ),
            errorElement,
            children: [
              {
                element: <FixedContent />,
                children: [
                  {
                    element: <RequireAcceptEulaGuard />,
                    children: [
                      {
                        element: <RequireOrgAccessGuard />,
                        children: fixedHeightRoutes
                      }
                    ]
                  }
                ]
              },
              {
                element: <ScrollContent />,
                children: [
                  {
                    element: <RequireAcceptEulaGuard />,
                    children: [
                      {
                        element: <RequireOrgAccessGuard />,
                        children: defaultLayoutRoutes.orgScoped
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            element: <ScrollContent />,
            children: [
              {
                element: <RequireAcceptEulaGuard />,
                children: defaultLayoutRoutes.general
              }
            ]
          }
        ]
      },

      {
        element: <ScrollContent />,
        children: [
          {
            path: path.inactiveOperation,
            Component: routeLazy(() => import('@features/operations/routes/InactiveOperation')),
            errorElement
          },
          {
            path: path.subscribeWithCode,
            Component: routeLazy(() => import('@features/operations/routes/SubscribeWithCode')),
            errorElement
          },
          {
            path: path.acceptCreateDealerInvite,
            Component: routeLazy(() => import('@features/dealer/routes/AcceptCreateDealerInvite')),
            errorElement
          }
        ]
      }
    ]
  }
]
